import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';
import WineA from '../images/wine-a.png';
import WineB from '../images/wine-b.png';
import WineC from '../images/wine-c.png';
import WineD from '../images/wine-d.png';
import WineE from '../images/wine-e.png';
import WineF from '../images/wine-f.png';
import WineG from '../images/wine-g.png';
import WineH from '../images/wine-h.png';
import points from '../images/puntuacion.png';
import pointsEn from '../images/puntuacion-en.jpg';

// Hasta 8 vinos (A–H), igual que ChooseWine. Los que no existen en el test
// se deshabilitan solos vía isWineDisabled().
const WINES = [
    { title: 'A', img: WineA },
    { title: 'B', img: WineB },
    { title: 'C', img: WineC },
    { title: 'D', img: WineD },
    { title: 'E', img: WineE },
    { title: 'F', img: WineF },
    { title: 'G', img: WineG },
    { title: 'H', img: WineH },
];

function CaIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'home':  return <svg {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
        case 'back':  return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'help':  return <svg {...p}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
        case 'x':     return <svg {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
        case 'shop':  return <svg {...p}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>;
        default: return null;
    }
}

export default function ChooseAnswer() {
    const { t, i18n } = useTranslation();
    const [testsData, setTestsData] = useState([]);
    const [answersData, setAnswersData] = useState([]);
    const [profileInfo, setProfileInfo] = useState({ id: 0 });
    const [helpVisible, setHelpVisible] = useState(false);
    const [ready, setReady] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) {
            const userInfo = JSON.parse(storedUserInfo);
            setProfileInfo(userInfo);
        }
    }, []);

    useEffect(() => {
        if (profileInfo && profileInfo.id) {
            const xhr = new XMLHttpRequest();
            xhr.open("GET", `${process.env.REACT_APP_API_URL}/getAnswers/${profileInfo.id}`, true);
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4 && xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    setTestsData(data.tests);
                    setAnswersData(data.answers);
                }
            };
            xhr.send();
        }
    }, [profileInfo]);

    const calculateTotalPoints = (answer) => (
        (answer.visual || 0) + (answer.olfactory || 0) +
        (answer.taste || 0) + (answer.variety || 0) + (answer.price || 0)
    );

    const handleWineClick = (e, wineTitle) => {
        const selectedTest = testsData.find(test => test.title === wineTitle);
        if (selectedTest) {
            const selectedAnswer = answersData.find(answer => Number(answer.test_id) === Number(selectedTest.id));
            if (selectedAnswer && selectedAnswer.object) {
                localStorage.setItem('currentTest', JSON.stringify(selectedTest));
                localStorage.setItem('currentAnswer', JSON.stringify(selectedAnswer.object));
            } else {
                e.preventDefault();
            }
        } else {
            e.preventDefault();
        }
    };

    const isWineDisabled = (wineTitle) => !testsData.some(test => test.title === wineTitle);

    const getPoints = (wineTitle) => {
        const test = testsData.find(t => t.title === wineTitle);
        if (!test) return null;
        const answer = answersData.find(a => Number(a.test_id) === Number(test.id));
        return answer ? calculateTotalPoints(answer) : null;
    };

    return (
        <div className={`ca-stage${ready ? ' ca-ready' : ''}`}>
            <div className="ca-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="ca-vignette" />
            <div className="ca-grain" />

            <div className="ca-topbar">
                <button className="ca-icon-btn" onClick={() => navigate('/')} aria-label={t('common.home')}>
                    <CaIcon name="home" size={16} />
                </button>
                <span className="ca-topbar-title">{t('test.correct_answers')}</span>
                <button className="ca-icon-btn" onClick={() => setHelpVisible(true)} aria-label={t('common.help')}>
                    <CaIcon name="help" size={16} />
                </button>
            </div>

            <div className="ca-panel">
                <h2 className="ca-heading">{t('choose_wine.title')}</h2>
                <div className="ca-bottles">
                    {WINES.map(({ title, img }, i) => {
                        const pts = getPoints(title);
                        const disabled = isWineDisabled(title);
                        return (
                            <Link
                                key={title}
                                to="/answer"
                                className={`ca-bottle-link${disabled ? ' ca-disabled' : ''}`}
                                onClick={(e) => handleWineClick(e, title)}
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                <img src={img} alt={`${t('choose_wine.wine')} ${title}`} className="ca-bottle-img" width="80" height="150" />
                                <span className="ca-bottle-label">{title}</span>
                                {pts !== null && <span className="ca-bottle-pts">{pts} pts</span>}
                            </Link>
                        );
                    })}
                </div>
            </div>

            <div className="ca-bottom">
                <button className="ca-bottom-btn" onClick={() => navigate(-1)}>
                    <CaIcon name="back" size={14} />{t('common.back')}
                </button>
                <button className="ca-bottom-btn ca-bottom-btn-buy"
                    onClick={() => window.open('http://www.winegang.es/vinos-del-mes', '_blank')}>
                    <CaIcon name="shop" size={14} />{t('choose_answer.buy')}
                </button>
            </div>

            {helpVisible && (
                <div className="ca-popup-overlay" onClick={() => setHelpVisible(false)}>
                    <div className="ca-popup" onClick={e => e.stopPropagation()}>
                        <button className="ca-popup-close" onClick={() => setHelpVisible(false)} aria-label={t('common.close')}>
                            <CaIcon name="x" size={14} />
                        </button>
                        <img src={i18n.language === 'en' ? pointsEn : points} alt={t('common.score')} className="ca-popup-img" />
                    </div>
                </div>
            )}
        </div>
    );
}
